import Component from '@glimmer/component';
import { tracked, cached } from '@glimmer/tracking';
import { action, set, setProperties } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import logger from 'donor-elf-web/lib/logger';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import V2TaskValidation from 'donor-elf-web/validations/v2-task';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import type {
  onCopyTaskCallback,
  onTaskDeletedCallback,
  onTaskSavedCallback,
  onTaskSaveErrorCallback,
  onTaskSavingCallback,
} from '.';
import AbilitiesService from 'donor-elf-web/services/abilities';
import FundsService from 'donor-elf-web/services/funds-service';
import JournalService from 'donor-elf-web/services/journal-service';
import Settings from 'donor-elf-web/services/settings';
import TaskService from 'donor-elf-web/services/task-service';
import { type SelectOption } from 'donor-elf-web/lib/form-component-utils';
import JournalModel from 'donor-elf-web/models/journal';
import Store from '@ember-data/store';
import BulkActionModel, {
  type BulkActionCustomAddTask,
} from 'donor-elf-web/models/bulk-action';
import dateUtils from 'donor-elf-web/lib/date-utils';

interface ComponentArgs {
  task: V2TaskModel;
  changeset?: any;
  displayJournalInfo: boolean;
  hideSaveAndCancel?: boolean; // default false
  showCopyIcon?: boolean; // default false
  copyAndPasteAllConfirmation?: string; // if present, it will show paste option with this confirmation message
  saveImmediately?: boolean; //if true it saves the task immediately
  inAddModal?: boolean; // set to true if this is in the global Add modal
  hideAddAnother?: boolean; // hide the 'Save & Add Another' button while editing the task
  onTaskSaving?: onTaskSavingCallback;
  onTaskSaved: onTaskSavedCallback;
  onTaskSaveError: onTaskSaveErrorCallback;
  onTaskDeleted: onTaskDeletedCallback;
  onCopyTask: onCopyTaskCallback;
  onCancel: () => void;
}

export default class TaskInfoEditComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);

    this.setChangeset();
    if (args.saveImmediately) {
      setTimeout(() => {
        this.save();
      });
    }
  }

  @service declare abilities: AbilitiesService;
  @service declare fundsService: FundsService;
  @service declare journalService: JournalService;
  @service declare settings: Settings;
  @service declare store: Store;
  @service declare taskService: TaskService;

  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError = false;
  @tracked deleting = false;
  @tracked bulkAction?: BulkActionModel;
  @tracked waitingOnBulkActionToComplete = false;
  @tracked initiallyHadJournalStage = false;
  @tracked showSelectActionBeforeMarkingComplete = false;
  @tracked confirmCopyAndPasteAll = false;

  @tracked changeset: any;

  @action
  setChangeset() {
    this.changeset =
      this.args.changeset ||
      Changeset(
        this.args.task,
        lookupValidator(V2TaskValidation),
        V2TaskValidation,
      );
  }

  get showCopyIcon() {
    return this.args.showCopyIcon ?? false;
  }

  get showCopyAndPasteAll() {
    return this.args.copyAndPasteAllConfirmation != null;
  }

  get showAddAnother() {
    return !this.savingForMultipleContacts && this.args.hideAddAnother !== true;
  }

  get savingForMultipleContacts() {
    return (this.args.task.contactIds?.length || 0) > 0;
  }

  get leftBarColor() {
    if (this.saveError) {
      return 'bg-danger';
    } else if (this.args.task.completedDate) {
      return 'bg-success';
    } else {
      return 'bg-secondary';
    }
  }

  get isOverdue() {
    return this.args.task.dueDate && this.args.task.dueDate < new Date();
  }

  get hasNotes() {
    return isPresent(this.args.task.notes);
  }

  get actionOptions() {
    return this.taskService.actionOptions;
  }

  get mediumOptions() {
    return this.taskService.mediumOptions;
  }

  get showResultOptionsAndCompletedDate() {
    return (
      isPresent(this.changeset.get('completedDate')) &&
      isPresent(this.resultOptions)
    );
  }

  get resultOptions() {
    return this.taskService.availableResultsByActionId[
      this.changeset.get('actionId')
    ]?.map((result) => {
      return {
        label: result.label,
        val: result.id,
      };
    });
  }

  get hasAssignedUsers() {
    return isPresent(this.changeset.get('assignedUserIds'));
  }

  _assignedUsersOptions?: SelectOption[];
  get assignedUsersOptions() {
    if (this._assignedUsersOptions) {
      return this._assignedUsersOptions;
    }
    return (this._assignedUsersOptions = (
      this.taskService.usersByFundId[this.fundsService.selectedFund!.id] || []
    ).map((x) => {
      return {
        label: x.displayName,
        val: x.id,
      };
    }));
  }

  @cached
  get activeJournalsForContact(): JournalModel[] {
    let contactId = this.changeset.get('contactId');
    if (contactId) {
      return this.journalService
        .activeJournalInfosForContactId(contactId)
        .map((x) => x.journal);
    } else {
      return [];
    }
  }

  get showJournalAndStages() {
    if (!this.args.displayJournalInfo) {
      return false;
    }

    const hasContact =
      this.savingForMultipleContacts ||
      isPresent(this.changeset.get('contactId'));

    return hasContact && this.journalService.activeJournals.length > 0;
  }

  // not used for @inAddModal
  get showJournalOptions() {
    return (
      this.args.displayJournalInfo &&
      this.activeJournalsForContact.length > 1 &&
      !this.initiallyHadJournalStage
    );
  }

  get journalOptions(): SelectOption[] {
    const journals = (
      [
        {
          label: '',
          val: null,
        },
      ] as SelectOption[]
    ).concat(
      this.journalService.activeJournals.map((journal) => {
        return {
          label: journal.label,
          val: journal.id,
        };
      }),
    );

    if (isPresent(this.args.task.journalStageId)) {
      const journalInfo = this.journalService.stageInfoById(
        this.args.task.journalStageId!,
      ).journalInfo;
      const journalId = journalInfo.journal.id;

      if (journals.find((j) => j.val === journalId) == null) {
        journals.push({
          label: journalInfo.journal.label,
          val: journalId,
        });
      }
    }

    return journals;
  }

  get showJournalStageOptions() {
    return this.args.displayJournalInfo && isPresent(this.selectedJournalId);
  }

  get journalStageOptions(): SelectOption[] {
    if (this.selectedJournalId == null) {
      return [];
    }
    let journalInfo = this.journalService.journalInfoById(
      this.selectedJournalId,
    );
    return (
      [
        {
          label: '',
          val: null,
        },
      ] as SelectOption[]
    ).concat(
      journalInfo.sortedStages
        .filter((x) => !x.decisionStage)
        .map((stage) => {
          return {
            label: stage.name,
            val: stage.id,
          };
        }),
    );
  }

  get showDelete() {
    return !this.saving && !this.args.task.isNew;
  }

  @action
  templateDidInsert() {
    if (this.args.inAddModal) {
      document.addEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('click', this.handleDocumentClicked);
    } else {
      if (
        this.args.task.isNew &&
        isPresent(this.args.task.journalStageId) &&
        isEmpty(this.args.task.actionId) &&
        isEmpty(this.args.task.mediumId)
      ) {
        this.setActionAndMediumFromJournalStage();
      }
      this.initiallyHadJournalStage = isPresent(this.args.task.journalStageId);
    }
  }

  @action
  templateWillDestroy() {
    if (this.args.inAddModal) {
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.removeEventListener('click', this.handleDocumentClicked);
    }
  }

  @action
  handleDocumentKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.args.onCancel();
    }
  }

  @action
  handleDocumentClicked(e: MouseEvent) {
    // cancel if the user clicked outside the modal and basic dropdown and no changes have been made
    if (
      this.changeset.get('isPristine') &&
      (e.target as HTMLElement).closest('.modal-content') == null &&
      (e.target as HTMLElement).closest('.ember-basic-dropdown-content') == null
    ) {
      this.args.onCancel();
    }
  }

  get selectedJournalId() {
    // If the task initially had a journal stage id, then the user can only
    // change the journal stage but not the journal itself
    if (isPresent(this.args.task.journalStageId)) {
      return this.journalService.stageInfoById(this.args.task.journalStageId!)
        .journalInfo.journal.id;
    } else if (
      this.activeJournalsForContact.length === 1 &&
      !this.args.inAddModal
    ) {
      return this.activeJournalsForContact[0].id;
    } else {
      return this.changeset.get('journalId');
    }
  }

  setActionAndMediumFromJournalStage() {
    let journalStageId = this.changeset.get('journalStageId');
    if (isPresent(journalStageId)) {
      let stageInfo = this.journalService.stageInfoById(journalStageId);
      setProperties(this.changeset, {
        actionId: stageInfo.stage.lastTaskActionId,
        mediumId: stageInfo.stage.lastTaskMediumId,
      });
    }
  }

  @action
  handleContactChanged(_contactId: string) {
    this.changeset.set('journalId', null);
    this.changeset.set('journalStageId', null);
  }

  @action
  handleActionChanged() {
    if (isPresent(this.changeset.get('actionId'))) {
      this.showSelectActionBeforeMarkingComplete = false;
      let resultId = this.changeset.get('resultId');
      if (isPresent(resultId)) {
        if (this.resultOptions.find((x) => x.val === resultId) == null) {
          this.changeset.set('resultId', this.resultOptions[0].val);
        }
      } else if (isPresent(this.changeset.get('completedDate'))) {
        this.changeset.set('resultId', this.resultOptions[0].val);
      }
    }
  }

  @action
  handleSummaryChanged(val: string) {
    set(this.changeset, 'summary', val);
  }

  @action
  handleNotesChanged(el: any) {
    let val = el.value;
    if (isEmpty(val)) {
      val = null;
    }
    set(this.changeset, 'notes', val);
  }

  @action
  handleJournalSelected(val: string) {
    this.changeset.set('journalId', val);
    this.changeset.set('journalStageId', null);
  }

  @action
  handleJournalStageSelected(val: string) {
    this.changeset.set('journalStageId', val);
    if (
      isPresent(val) &&
      isEmpty(this.changeset.get('actionId')) &&
      isEmpty(this.changeset.get('mediumId'))
    ) {
      this.setActionAndMediumFromJournalStage();
    }
  }

  @action
  markComplete() {
    if (isEmpty(this.changeset.get('actionId'))) {
      this.showSelectActionBeforeMarkingComplete = true;
      return;
    }
    setProperties(this.changeset, {
      completedDate: new Date(),
      resultId: this.resultOptions?.[0].val,
    });
  }

  @action
  markNotComplete() {
    this.changeset.set('completedDate', null);
    this.changeset.set('resultId', null);
  }

  @action
  handleCopyDropdownOpened(dropdown: any) {
    this.confirmCopyAndPasteAll = false;
    if (!this.showCopyAndPasteAll) {
      this.args.onCopyTask({ task: this.args.task, pasteAll: false });
      setTimeout(() => {
        if (!this.isDestroying && !this.isDestroyed) {
          dropdown.actions.close();
        }
      }, 2000);
    }
  }

  @action
  handleCopy() {
    this.args.onCopyTask({ task: this.args.task, pasteAll: false });
  }

  @action
  handleCopyAndPasteAll() {
    this.confirmCopyAndPasteAll = true;
  }

  @action
  handleConfirmCopyAndPasteAll() {
    this.confirmCopyAndPasteAll = false;
    this.args.onCopyTask({ task: this.args.task, pasteAll: true });
  }

  @action
  saveAndAddAnother() {
    this.save({ addAnother: true });
  }

  @action
  async save(args: { addAnother?: boolean } = {}) {
    // seeing a potential issue where the save is getting kicked off twice
    // https://secure.helpscout.net/conversation/2839696328/10753?viewId=8504878
    if (this.saving) {
      return;
    }

    this.saving = true;
    this.showValidationErrors = false;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      try {
        this.args.onTaskSaving?.({ saving: true, task: this.args.task });
        if (this.savingForMultipleContacts) {
          await this.saveForMultipleContacts();
        } else {
          await this.saveForSingleContact(args);
        }
      } catch (error) {
        if (!this.args.task.isNew) {
          this.args.task.rollbackAttributes();
        }
        this.args.onTaskSaving?.({ saving: false, task: this.args.task });
        this.args.onTaskSaveError?.({ task: this.args.task, error: error });
        this.saving = false;
        this.saveError = true;
        logger.error(error);
      }
    } else {
      this.saving = false;
      this.showValidationErrors = true;
    }
  }

  async saveForSingleContact(args: { addAnother?: boolean }) {
    let wasNew = this.args.task.isNew;
    await this.changeset.save();
    if (wasNew && isPresent(this.args.task.journalStageId)) {
      let stage = this.journalService.stageInfoById(
        this.args.task.journalStageId!,
      ).stage;
      setProperties(stage, {
        lastTaskActionId: this.args.task.actionId,
        lastTaskMediumId: this.args.task.mediumId,
      });
      await stage.save();
    }
    this.saving = false;
    this.args.onTaskSaving?.({ saving: false, task: this.args.task });
    this.args.onTaskSaved(this.args.task, { addAnother: args.addAnother });
  }

  async saveForMultipleContacts() {
    this.changeset.execute();
    let task = this.changeset.data as V2TaskModel;
    let bulkAction = this.store.createRecord('bulk-action');
    bulkAction.actionType = 'add_task';
    (bulkAction.custom as BulkActionCustomAddTask) = {
      contact_ids: task.contactIds as string[],
      action_id: task.actionId!,
      medium_id: task.mediumId,
      due_date: dateUtils.serializeToDateOnly(task.dueDate),
      completed_date: dateUtils.serializeToDateOnly(task.completedDate),
      result_id: task.resultId,
      summary: task.summary,
      notes: task.notes,
      journal_stage_id: task.journalStageId,
      assigned_user_ids: task.assignedUserIds,
    };
    await bulkAction.save();
    this.bulkAction = bulkAction;
    this.waitingOnBulkActionToComplete = true;
  }

  @action
  async handleBulkActionStatusChange(bulkAction: BulkActionModel) {
    if (bulkAction.status === 'completed') {
      await bulkAction.destroyRecord();
      this.saving = false;
      this.args.onTaskSaving?.({ saving: false, task: this.args.task });
      this.args.onTaskSaved(this.args.task, { addAnother: false });
    } else if (bulkAction.status === 'error') {
      this.args.onTaskSaving?.({ saving: false, task: this.args.task });
      this.args.onTaskSaveError?.({ task: this.args.task, error: {} });
      this.saving = false;
      this.saveError = true;
    }
  }

  @action
  async delete() {
    this.deleting = true;
    this.showValidationErrors = false;
    try {
      await this.args.task.destroyRecord();
      this.args.onTaskDeleted(this.args.task);
    } catch (err) {
      this.deleting = false;
      this.saveError = true;
    }
  }
}
